import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getIdToken } from '../auth/cognito';

const WS_URL = import.meta.env.VITE_WS_URL as string | undefined;
const KEEPALIVE_MS = 5 * 60_000; // 5 min — under the 10-min idle timeout
const RECONNECT_MS = 3_000;

export function useRealtimeSync() {
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const keepaliveRef = useRef<ReturnType<typeof setInterval>>();
  const reconnectRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!WS_URL) return;

    let disposed = false;

    async function connect() {
      if (disposed) return;

      const token = await getIdToken();
      if (!token || disposed) return;

      const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;

      ws.onopen = () => {
        keepaliveRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: 'ping' }));
          }
        }, KEEPALIVE_MS);
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'notes-changed') {
            qc.invalidateQueries({ queryKey: ['notes'] });
          }
        } catch {
          // ignore non-JSON messages
        }
      };

      ws.onclose = () => {
        clearInterval(keepaliveRef.current);
        if (!disposed) {
          reconnectRef.current = setTimeout(connect, RECONNECT_MS);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    connect();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const ws = wsRef.current;
        if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
          clearTimeout(reconnectRef.current);
          connect();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      disposed = true;
      clearInterval(keepaliveRef.current);
      clearTimeout(reconnectRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
      wsRef.current?.close();
    };
  }, [qc]);
}
